import { HttpClient } from "../../http/HttpClient";
import { IContactVerificationService } from "@/core/requests/services/ContactVerificationService";
import {
  ContactVerificationConfirmRequest,
  ContactVerificationInitiateRequest,
  ContactVerificationResponse,
} from "@/core/requests/entities/ContactVerification";
import {
  RepositoryResult,
  RepositoryResultFactory,
} from "@/core/shared/types/RepositoryResult";
import { ApiResponse } from "../../api/types/ApiResponse";
import { ApiResponseHandler } from "../../http/services/handleApiResponse/handleApiResponse";
import {
  confirmContactVerificationUrl,
  initiateContactVerificationUrl,
} from "../../api/contactVerificationEndpoints/contactVerificationEndpoints";
import handleRequestError from "../../http/services/handleRequestError/handleRequestError";

export default class ContactVerificationRepositoryBackend implements IContactVerificationService {
  constructor(private readonly httpClient: HttpClient) {}

  async initiate(
    request: ContactVerificationInitiateRequest,
  ): Promise<RepositoryResult<ContactVerificationResponse>> {
    try {
      const url = initiateContactVerificationUrl();

      const response = await this.httpClient.post<
        ApiResponse<{
          transactionId: string;
          token: string;
          status: string;
          expiresAt: string;
          message: string;
        }>
      >({
        url,
        body: {
          businessKey: request.businessKey,
          channel: request.channel,
          destination: request.destination,
          countryCode: request.countryCode,
        },
        withAuth: true,
      });

      const result = ApiResponseHandler.handleResponse<{
        transactionId: string;
        token: string;
        status: string;
        expiresAt: string;
        message: string;
      }>(response);

      if (result.success && result.data) {
        const mapped: ContactVerificationResponse = {
          code: response.code,
          message: response.message,
          data: {
            transactionId: result.data.transactionId,
            token: result.data.token,
            status: result.data.status,
            expiresAt: result.data.expiresAt,
            message: result.data.message,
          },
        };
        return RepositoryResultFactory.success(mapped);
      }

      return RepositoryResultFactory.error(
        ApiResponseHandler.createExceptionFromResponse(response),
      );
    } catch (error) {
      return RepositoryResultFactory.error(
        handleRequestError(error as ApiResponse<unknown>),
      );
    }
  }

  async confirm(
    request: ContactVerificationConfirmRequest,
  ): Promise<RepositoryResult<ContactVerificationResponse>> {
    try {
      const url = confirmContactVerificationUrl();

      const response = await this.httpClient.post<
        ApiResponse<{
          transactionId: string;
          token: string;
          status: string;
          expiresAt: string;
          message: string;
        }>
      >({
        url,
        body: {
          transactionId: request.transactionId,
          code: request.code,
        },
        withAuth: true,
      });

      const result = ApiResponseHandler.handleResponse<{
        transactionId: string;
        token: string;
        status: string;
        expiresAt: string;
        message: string;
      }>(response);

      if (result.success && result.data) {
        const mapped: ContactVerificationResponse = {
          code: response.code,
          message: response.message,
          data: {
            transactionId: result.data.transactionId,
            token: result.data.token,
            status: result.data.status,
            expiresAt: result.data.expiresAt,
            message: result.data.message,
          },
        };
        return RepositoryResultFactory.success(mapped);
      }

      return RepositoryResultFactory.error(
        ApiResponseHandler.createExceptionFromResponse(response),
      );
    } catch (error) {
      return RepositoryResultFactory.error(
        handleRequestError(error as ApiResponse<unknown>),
      );
    }
  }
}
